import React from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

import { AuthCtx } from 'components/auth/auth-ctx';
import { useAuth } from 'components/auth/auth-hook';
import { ScrollToTop } from 'components/router/scroll-to-top';
import { Header } from 'components/header/header';
import { Footer } from 'components/footer/footer';
import { Portfolio } from 'components/portfolio/portfolio';
import {
  NotificationCtx,
  useNotification,
} from 'components/notification/notification-hook';
import { Notification } from 'components/notification/notification';
import { useI18n, I18nCtx } from 'components/i18n/i18n-context';
import { OrderCtxProvider } from 'components/order-ctx/order-ctx-provider';
import { ReworkCtxProvider } from 'components/rework-ctx/rework-ctx-provider';

import { Homepage } from 'modules/homepage/homepage';
import { Registration } from 'modules/registration/registration';
import { UserProfile } from 'modules/portfolio-user-profile/user-profile';
import { ResearchSheet } from 'modules/portfolio-research-sheet/research-sheet';
import { ResearchSheetNew } from 'modules/portfolio-research-sheet/research-sheet-new';
import { ResearchSheetRework } from 'modules/portfolio-research-sheet/research-sheet-rework';
import { ArchivalAidPrivateGrid } from 'modules/archival-aid/archival-aid-private/archival-aid-private-grid';
import { ArchivalUnitGrid } from 'modules/archival-unit/archival-unit-grid';
import { RegisterGrid } from 'modules/register/register-grid';
import { OrderNew } from 'modules/portfolio-order/order-new';
import { OrderRework } from 'modules/portfolio-order/order-rework';
import { OrderHistory } from 'modules/portfolio-order/order-history';
import { Favourite } from 'modules/portfolio-favourite/favourite';
import { RegistrationForm } from 'modules/registration/registration-form';
import { LentDigital } from 'modules/portfolio-lent/lent-digital/lent-digital';
import { LentTablePhysical } from 'modules/portfolio-lent/lent-table-physical';
import { LentHistoryTablePhysical } from 'modules/portfolio-lent/history/lent-history-table-physical';
import { LentHistoryTableDigital } from 'modules/portfolio-lent/history/lent-history-table-digital';
import { LentHistoryTableRegistered } from 'modules/portfolio-lent/history/lent-history-table-registered';
import { RedirectFromESM } from 'modules/redirect-from-esm/redirect-from-esm';
import { RedirectFromAdmin } from 'modules/redirect-from-admin/redirect-from-admin';
import { ResearcherNewOrder } from 'modules/portfolio-order/researcher-new-order';
import { ArchivalUnitPublicGrid } from 'modules/archival-unit-private/archival-unit-private-grid';
import { DialogProvider } from 'components/dialog/dialog-context-provider';
import { Guide } from 'modules/guide/guide';

import './app.css';
import MainGrid from 'components/grid/grid-ctx/MainGrid';
import { EadTreeContextProvider } from 'components/grid/grid-ctx/EadTreeCtx';
import { VariantContextProvider } from 'components/grid/grid-ctx/VariantCtx';
import MapPage, { MapPageOriginator } from 'modules/map';
import NotFound from 'modules/not-found';
import ArchivalUnitSwitch from 'modules/archival-unit/archival-unit-switch';
import { Search } from 'modules/search/original/search';

export function App() {
  const authCtx = useAuth();
  const i18ctx = useI18n();

  const { notificationContext, notification } = useNotification({
    timeout: 0,
  });

  if (process.env.NODE_ENV === 'production' && !authCtx.userLoaded) {
    return <></>;
  }

  return (
    <AuthCtx.Provider value={authCtx}>
      <NotificationCtx.Provider value={notificationContext}>
        <Notification
          variant={notification.variant}
          message={notification.message}
          open={notification.open}
          onClose={notificationContext.closeNotification}
        />
        <I18nCtx.Provider value={i18ctx}>
          <IntlProvider locale={i18ctx.locale} messages={i18ctx.messages}>
            <Router basename={process.env.PUBLIC_URL}>
              <EadTreeContextProvider>
                <ReworkCtxProvider>
                  <OrderCtxProvider>
                    <DialogProvider>
                      <ScrollToTop />
                      <Header />
                      <div className="content">
                        <Switch>
                          <Route path="/map">
                            <Route path={['/map/originator/:index/:id']}>
                              <MapPageOriginator />
                            </Route>
                            <Route path={['/map/ead/:index/:id']} exact>
                              <MapPage />
                            </Route>
                          </Route>
                          <Route
                            path={[
                              '/fondy/:level/:id/:ead',
                              '/fondy/:level/:id',
                              '/fondy',
                            ]}
                          >
                            <VariantContextProvider variant="funds">
                              <MainGrid />
                            </VariantContextProvider>
                          </Route>
                          <Route
                            path={[
                              '/pomucky/:level/:id/:ead',
                              '/pomucky/:level/:id',
                              '/pomucky',
                            ]}
                          >
                            <VariantContextProvider variant="aids">
                              <MainGrid />
                            </VariantContextProvider>
                          </Route>
                          <Route
                            path={['/archivalie/:level/:id', '/archivalie']}
                          >
                            <ArchivalUnitGrid />
                          </Route>
                          <Route path={['/archivalie-switch/:id']}>
                            <ArchivalUnitSwitch />
                          </Route>

                          <Route
                            path={[
                              '/soukrome-archivalie/:level/:id',
                              '/soukrome-archivalie',
                            ]}
                          >
                            <ArchivalUnitPublicGrid />
                          </Route>
                          <Route
                            path={[
                              '/soukrome-pomucky/:level/:id',
                              '/soukrome-pomucky',
                            ]}
                          >
                            <ArchivalAidPrivateGrid />
                          </Route>
                          <Route path={['/rejstriky-detail/:id']} exact>
                            <RegisterGrid detailView />
                          </Route>
                          <Route
                            path={[
                              '/rejstriky/:originatorClass/:search/:page/:rowsPerPage/:id',
                              '/rejstriky/:originatorClass/:search/:page/:rowsPerPage',
                              '/rejstriky/:originatorClass/:search/:page',
                              '/rejstriky/:originatorClass/:search',
                              '/rejstriky/:originatorClass',
                              '/rejstriky',
                            ]}
                          >
                            <RegisterGrid />
                          </Route>
                          <Route path="/registrace">
                            <Registration />
                          </Route>
                          <Route path="/registracni-formular">
                            <RegistrationForm />
                          </Route>
                          <Route path="/portfolio">
                            <Portfolio>
                              <Route
                                exact
                                path="/portfolio/badatelske-listy/novy"
                              >
                                <ResearchSheetNew />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/badatelske-listy/oprava"
                              >
                                <ResearchSheetRework />
                              </Route>
                              <Route exact path="/portfolio/badatelske-listy">
                                <ResearchSheet />
                              </Route>
                              <Route exact path="/portfolio/profil">
                                <UserProfile />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/zadat-badatelsky-dotaz"
                              >
                                <ResearcherNewOrder />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/vytvoreni-objednavky"
                              >
                                <OrderNew />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/historie-objednavek"
                              >
                                <OrderHistory />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/historie-objednavek/oprava"
                              >
                                <OrderRework />
                              </Route>
                              <Route exact path="/portfolio/vypujcky/digitalni">
                                <LentDigital />
                              </Route>
                              <Route exact path="/portfolio/vypujcky/papirove">
                                <LentTablePhysical />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/historie-vypujcek/digitalni"
                              >
                                <LentHistoryTableDigital />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/historie-vypujcek/papirove"
                              >
                                <LentHistoryTablePhysical />
                              </Route>
                              <Route
                                exact
                                path="/portfolio/historie-vypujcek/registered"
                              >
                                <LentHistoryTableRegistered />
                              </Route>
                              <Route exact path="/portfolio/oblibene">
                                <Favourite />
                              </Route>
                            </Portfolio>
                          </Route>
                          <Route path="/redirect/:redirectFrom/:id">
                            <RedirectFromESM />
                          </Route>
                          <Route path="/redirect-admin/:sheetId">
                            <RedirectFromAdmin />
                          </Route>
                          <Route path="/napoveda">
                            <Guide />
                          </Route>
                          <Route path="/hledat">
                            <Search />
                          </Route>
                          <Route path="/not-found">
                            <NotFound />
                          </Route>
                          <Route path="/">
                            <Homepage />
                          </Route>
                        </Switch>
                      </div>
                      <Footer />
                    </DialogProvider>
                  </OrderCtxProvider>
                </ReworkCtxProvider>
              </EadTreeContextProvider>
            </Router>
          </IntlProvider>
        </I18nCtx.Provider>
      </NotificationCtx.Provider>
    </AuthCtx.Provider>
  );
}
